/**
 * Formatting Hooks for Platform and User Settings
 * 
 * This module provides React hooks and utility functions that format
 * dates, times, currencies, and numbers according to settings.
 * 
 * Priority: User Settings > Platform Settings (defaults)
 * 
 * Usage:
 *   const { formatDate, formatTime, formatCurrency, formatNumber } = useFormatters();
 *   return <span>{formatDate(someDate)}</span>;
 */

import { useContext, useMemo, useCallback } from "react";
import { usePlatformSettings } from "../settings/PlatformSettingsContext";
import UserSettingsContext from "../settings/UserSettingsContext";

// Currency symbol mapping
const CURRENCY_SYMBOLS = {
  GBP: "£",
  EUR: "€",
  USD: "$",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

// Locale mapping for number formatting
const NUMBER_FORMAT_LOCALES = {
  "1,234.56": "en-US",
  "1.234,56": "de-DE",
  "1 234,56": "fr-FR",
};

/**
 * Hook to get all formatting functions based on merged user/platform settings
 */
export function useFormatters() {
  // Try to get user settings context (may not be available if not wrapped)
  const userSettingsContext = useContext(UserSettingsContext);
  
  // Get platform settings as fallback
  const platformSettingsContext = usePlatformSettings();
  
  // Use merged settings from UserSettings if available, otherwise platform
  const settings = useMemo(() => {
    if (userSettingsContext?.settings) {
      return userSettingsContext.settings;
    }
    return platformSettingsContext.settings;
  }, [userSettingsContext?.settings, platformSettingsContext.settings]);

  // Format date according to settings
  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return "-";
    
    try {
      const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
      
      if (isNaN(date.getTime())) return String(dateInput);
      
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      
      switch (settings?.date_format) {
        case "DD/MM/YYYY":
          return `${day}/${month}/${year}`;
        case "MM/DD/YYYY":
          return `${month}/${day}/${year}`;
        case "YYYY-MM-DD":
          return `${year}-${month}-${day}`;
        default:
          return `${day}/${month}/${year}`;
      }
    } catch {
      return String(dateInput);
    }
  }, [settings?.date_format]);

  // Format time according to settings
  const formatTime = useCallback((timeInput) => {
    if (!timeInput) return "-";
    
    try {
      let hours, minutes;
      
      if (typeof timeInput === "string" && timeInput.includes(":")) {
        [hours, minutes] = timeInput.split(":").map(Number);
      } else if (timeInput instanceof Date) {
        hours = timeInput.getHours();
        minutes = timeInput.getMinutes();
      } else {
        return String(timeInput);
      }
      
      if (settings?.time_format === "12-hour") {
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
      } else {
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      }
    } catch {
      return String(timeInput);
    }
  }, [settings?.time_format]);

  // Get currency symbol
  const getCurrencySymbol = useCallback(() => {
    return CURRENCY_SYMBOLS[settings?.currency] || "£";
  }, [settings?.currency]);

  // Format currency according to settings
  const formatCurrency = useCallback((amount) => {
    if (amount === null || amount === undefined) return "-";
    
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) return "-";
    
    const symbol = CURRENCY_SYMBOLS[settings?.currency] || "£";
    const locale = NUMBER_FORMAT_LOCALES[settings?.number_format] || "en-GB";
    
    const formatted = num.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return `${symbol}${formatted}`;
  }, [settings?.currency, settings?.number_format]);

  // Format number according to settings
  const formatNumber = useCallback((value, decimals = 2) => {
    if (value === null || value === undefined) return "-";
    
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    
    const locale = NUMBER_FORMAT_LOCALES[settings?.number_format] || "en-GB";
    
    return num.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, [settings?.number_format]);

  // Format datetime (date + time combined)
  const formatDateTime = useCallback((dateInput) => {
    if (!dateInput) return "-";
    const dateStr = formatDate(dateInput);
    
    try {
      const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const timeStr = formatTime(`${hours}:${minutes}`);
      return `${dateStr} ${timeStr}`;
    } catch {
      return dateStr;
    }
  }, [formatDate, formatTime]);

  // Format duration in hours
  const formatDuration = useCallback((hours) => {
    if (hours === null || hours === undefined) return "-";
    const num = typeof hours === "string" ? parseFloat(hours) : hours;
    if (isNaN(num)) return "-";
    return `${num.toFixed(1)}h`;
  }, []);

  // Current currency symbol
  const currencySymbol = getCurrencySymbol();

  return {
    formatDate,
    formatTime,
    formatDateTime,
    formatCurrency,
    formatNumber,
    formatDuration,
    currencySymbol,
    getCurrencySymbol,
    settings,
  };
}

/**
 * Static formatters for use outside React components (e.g., in table column definitions)
 * These use default formats but are overridden when the component uses useFormatters
 * 
 * NOTE: Prefer using useFormatters() hook inside components for settings-aware formatting
 */
export const staticFormatters = {
  formatDate: (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(dateString);
    }
  },
  
  formatCurrency: (amount, symbol = "£") => {
    if (amount === null || amount === undefined) return "-";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) return "-";
    return `${symbol}${num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },
  
  formatTime: (timeString) => {
    if (!timeString) return "-";
    return timeString;
  },
  
  formatDuration: (hours) => {
    if (hours === null || hours === undefined) return "-";
    const num = typeof hours === "string" ? parseFloat(hours) : hours;
    if (isNaN(num)) return "-";
    return `${num.toFixed(1)}h`;
  },
};

export default useFormatters;
